import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Search, Package } from "lucide-react";

/**
 * Search-as-you-type product+variant picker.
 * options: array of { productId, variantId, label, stock, image_base64 } — flat rows to pick from.
 * value: { productId, variantId } | null
 */
export default function ProductAutocomplete({ options, value, onChange, placeholder = "Escrever produto…", testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  const selected = useMemo(() => {
    if (!value?.productId || !value?.variantId) return null;
    return options.find((o) => o.productId === value.productId && o.variantId === value.variantId) || null;
  }, [options, value]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => { if (selected && !query) setQuery(selected.label); }, [selected]); // eslint-disable-line

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 20)
    : options.slice(0, 20);

  const pick = (opt) => {
    onChange({ productId: opt.productId, variantId: opt.variantId });
    setQuery(opt.label);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (!open) return;
            if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
            else if (e.key === "Enter") { if (filtered[highlight]) { e.preventDefault(); pick(filtered[highlight]); } }
            else if (e.key === "Escape") setOpen(false);
          }}
          placeholder={placeholder}
          className="pl-9"
          autoComplete="off"
          data-testid={testid}
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-border rounded-md shadow-lg overflow-hidden max-h-80 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">Nenhum resultado</div>
          ) : filtered.map((opt, i) => (
            <button
              key={`${opt.productId}-${opt.variantId}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 ${i === highlight ? "bg-[hsl(var(--secondary))]" : ""}`}
              data-testid={`${testid}-option-${i}`}
            >
              <div className="w-9 h-9 rounded-md bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                {opt.image_base64 ? <img src={opt.image_base64} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-muted-foreground/60" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.stock} em stock</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
